import { Box, Button, Icon } from '@front10/base-elements';
import { useDirection, useTranslation } from '@front10/utils';
import dayjs from 'dayjs';
import React, { KeyboardEventHandler, PropsWithChildren, useRef } from 'react';
import { useFullMonths } from '../i18n';
import { CommonCalendarState } from '../types';
import { subMonthsNumber } from './helper';
import { CalendarDispatchAction, useCalendarActions } from './hooks';

export type CalendarHeaderProps = {
  showDecreaseYearButton: boolean;
  showDecreaseMonthButton: boolean;
  showIncreaseYearButton: boolean;
  showIncreaseMonthButton: boolean;
  enabledDecreaseYearButton: boolean;
  enabledIncreaseYearButton: boolean;
  iconNextProps: any;
  iconPrevProps: any;
  iconPrevYearProps: any;
  iconNextYearProps: any;
  containerSettings: any;
  style: any;
  dispatch: CalendarDispatchAction;
  state: CommonCalendarState;
  numberOfMonths: number;
  format?: string;
  currentMonth?: number;
  accessibilityState?: boolean;
  focusOnlyUserSelected?: boolean;
};

const CalendarHeader = (props: PropsWithChildren<CalendarHeaderProps>) => {
  const {
    showDecreaseYearButton = false,
    showDecreaseMonthButton = true,
    showIncreaseYearButton = false,
    showIncreaseMonthButton = true,
    enabledDecreaseYearButton = false,
    enabledIncreaseYearButton = false,
    dispatch,
    state,
    numberOfMonths,
    iconNextProps = {},
    iconPrevProps = {},
    iconNextYearProps = {},
    iconPrevYearProps = {},
    // containerSettings = {},
    children,
    currentMonth,
    accessibilityState,
    focusOnlyUserSelected = true
  } = props;

  const direction = useDirection();
  const translate = useTranslation();
  const previousMonthRef = useRef<HTMLButtonElement>(null);
  const nextMonthRef = useRef<HTMLButtonElement>(null);
  const renderMessagesIsDisablesNextButton = useRef(false);
  const renderMessagesIsDisablesPreviousButton = useRef(false);
  const {
    setNextMonth,
    setPreviousMonth,
    setNextYear,
    setPreviousYear,
    isEnabledNextMonth,
    isEnabledPreviousMonth
  } = useCalendarActions(focusOnlyUserSelected);

  const date = state.months[0].firstAvailableDay();
  const monthsLabels = useFullMonths();

  const previousMonth = subMonthsNumber(
    state.displayDate ? state.displayDate : state.today,
    numberOfMonths === 1 ? numberOfMonths : numberOfMonths - 1
  );

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === 'ArrowLeft' && isEnabledPreviousMonth) {
      setPreviousMonth();
      dispatch({ type: 'SET_PRESELECTED_DATE', payload: date });
      previousMonthRef.current?.focus();
    } else if (event.key === 'ArrowRight' && isEnabledNextMonth) {
      setNextMonth();
      dispatch({ type: 'SET_PRESELECTED_DATE', payload: date });
      nextMonthRef.current?.focus();
    }
  };
  const renderAccessibilityMessages = accessibilityState ? (
    <div className="fr-sr-only" role="alert" key={currentMonth}>
      {`
  ${translate('cmp-datepicker-show-month', 'Current month')} 
  ${
    monthsLabels[
      new Date(
        previousMonth.getFullYear(),
        numberOfMonths === 1
          ? previousMonth.getMonth() + numberOfMonths
          : previousMonth.getMonth() + numberOfMonths - 1,
        1
      ).getMonth()
    ]
  }
  ${new Date(
    previousMonth.getFullYear(),
    numberOfMonths === 1
      ? previousMonth.getMonth() + numberOfMonths
      : previousMonth.getMonth() + numberOfMonths - 1,
    1
  ).getFullYear()}
`}

      {currentMonth && currentMonth >= 10 && isEnabledPreviousMonth
        ? `
    ${translate('cmp-datepicker-prev-month', 'Previous month')},
    ${
      monthsLabels[
        new Date(
          previousMonth.getFullYear(),
          numberOfMonths === 1
            ? previousMonth.getMonth()
            : previousMonth.getMonth() + numberOfMonths - 2,
          1
        ).getMonth()
      ]
    }
    `
        : !isEnabledPreviousMonth &&
          dayjs(state.displayDate).get('month') > dayjs(state.today).get('month') &&
          currentMonth &&
          currentMonth >= 10
        ? `
          ${translate('cmp-datepicker-prev-month', 'Previous month')},
          ${
            monthsLabels[
              new Date(
                previousMonth.getFullYear(),
                numberOfMonths === 1
                  ? previousMonth.getMonth()
                  : previousMonth.getMonth() + numberOfMonths - 2,
                1
              ).getMonth()
            ]
          }
          `
        : null}

      {currentMonth && currentMonth >= 5 && currentMonth < 10 && isEnabledNextMonth
        ? `
    ${translate('cmp-datepicker-next-month', 'Next month')},
    ${
      monthsLabels[
        new Date(
          previousMonth.getFullYear(),
          numberOfMonths === 1
            ? previousMonth.getMonth() + numberOfMonths + 1
            : previousMonth.getMonth() + numberOfMonths,
          1
        ).getMonth()
      ]
    }
    `
        : !isEnabledNextMonth &&
          dayjs(state.displayDate).get('month') <
            dayjs(state.today)
              .add(state.availableDays ?? 0, 'day')
              .get('month') &&
          currentMonth &&
          currentMonth >= 5 &&
          currentMonth < 10
        ? `
                       ${translate('cmp-datepicker-next-month', 'Next month')},
                       ${
                         monthsLabels[
                           new Date(
                             previousMonth.getFullYear(),
                             numberOfMonths === 1
                               ? previousMonth.getMonth() + numberOfMonths + 1
                               : previousMonth.getMonth() + numberOfMonths,
                             1
                           ).getMonth()
                         ]
                       }
                       `
        : null}
      {renderMessagesIsDisablesNextButton.current
        ? translate('cmp-datepicker-next-month-button-disabled', 'Next month button disabled')
        : ''}
      {renderMessagesIsDisablesPreviousButton.current
        ? translate(
            'cmp-datepicker-previous-month-button-disabled',
            'Previous month button disabled'
          )
        : ''}
    </div>
  ) : null;

  return (
    <Box className="calendar-header" onKeyDown={handleKeyDown}>
      {renderAccessibilityMessages}
      <div className="calendar-header-button-container">
        <div className="btn-wrapper-left fr-d-flex fr-align-items-center">
          {showDecreaseYearButton && (
            <button
              type="button"
              name={'Previous year'}
              disabled={!enabledDecreaseYearButton}
              aria-disabled={!enabledDecreaseYearButton}
              onClick={setPreviousYear}
              className={`calendar-button fr-mr-1`}
              // tabIndex={tabIndex}
            >
              <Icon
                icon={direction === 'rtl' ? 'right-chevron' : 'left-chevron'}
                {...iconPrevProps}
                {...iconPrevYearProps}
              />
            </button>
          )}
          {showDecreaseMonthButton && (
            <Button
              ref={previousMonthRef}
              type="button"
              sx={{
                cursor: !isEnabledPreviousMonth ? 'default' : 'pointer'
              }}
              name={translate('cmp-datepicker-prev-month', 'Previous month')}
              aria-disabled={!isEnabledPreviousMonth}
              onClick={() => {
                if (isEnabledPreviousMonth) {
                  const newCurrentMonth = Math.random() + 10;
                  dispatch({
                    type: 'CHANGE_CURRENT_MONTH',
                    payload: newCurrentMonth
                  });
                  setPreviousMonth();
                } else {
                  renderMessagesIsDisablesPreviousButton.current = true;
                  const newCurrentMonth = Math.random() + 10;
                  dispatch({
                    type: 'CHANGE_CURRENT_MONTH',
                    payload: newCurrentMonth
                  });
                  setTimeout(() => {
                    renderMessagesIsDisablesPreviousButton.current = false;
                  }, 500);
                }
              }}
              className={`calendar-button ${!isEnabledPreviousMonth ? 'fr-btn-disabled' : ''}`}
              aria-label={translate(
                'cmp-datepicker-previous-month-accessibility-aria-label',
                'Previous month'
              )}
            >
              <Icon
                icon={direction === 'rtl' ? 'right-chevron' : 'left-chevron'}
                {...iconPrevProps}
              />
            </Button>
          )}
        </div>
        <div className="btn-wrapper-right fr-d-flex fr-align-items-center">
          {showIncreaseMonthButton && (
            <Button
              ref={nextMonthRef}
              type="button"
              sx={{ cursor: !isEnabledNextMonth ? 'default' : 'pointer' }}
              name={translate('cmp-datepicker-next-month', 'Next month')}
              aria-disabled={!isEnabledNextMonth}
              aria-label={translate(
                'cmp-datepicker-next-month-accessibility-aria-label',
                'Next month'
              )}
              onClick={() => {
                if (isEnabledNextMonth) {
                  const newCurrentMonth = Math.random() + 5;
                  dispatch({
                    type: 'CHANGE_CURRENT_MONTH',
                    payload: newCurrentMonth
                  });
                  setNextMonth();
                } else {
                  renderMessagesIsDisablesNextButton.current = true;
                  const newCurrentMonth = Math.random() + 5;
                  dispatch({
                    type: 'CHANGE_CURRENT_MONTH',
                    payload: newCurrentMonth
                  });
                  setTimeout(() => {
                    renderMessagesIsDisablesNextButton.current = false;
                  }, 500);
                }
              }}
              className={`calendar-button ${!isEnabledNextMonth ? 'fr-btn-disabled' : ''}`}
            >
              <Icon
                icon={direction === 'rtl' ? 'left-chevron' : 'right-chevron'}
                {...iconNextProps}
              />
            </Button>
          )}
          {showIncreaseYearButton && (
            <button
              type="button"
              name={'Next year'}
              disabled={!enabledIncreaseYearButton}
              aria-disabled={!enabledIncreaseYearButton}
              onClick={setNextYear}
              className="calendar-button fr-ml-1"
            >
              <Icon
                icon={direction === 'rtl' ? 'left-chevron' : 'right-chevron'}
                {...iconNextProps}
                {...iconNextYearProps}
              />
            </button>
          )}
        </div>
      </div>
      {children}
    </Box>
  );
};

export default CalendarHeader;
